import { useEffect, useState } from 'react';
import { fetchAPI, postAPI, deleteAPI } from '../../utils/utils';
import UserInfo from '../login/UserInfo';
import HeaderTabs from '../interface/HeaderTabs';
import Pressable from '../other/Pressable';
import WorldMap from './WorldMap';
import IpWatchlist from './IpWatchlist';
import './Mapa.css';

function MapaAccionsApp({ userInfo, castellersInfo, socket, ...props }) {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(7);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [visibleIntervals, setVisibleIntervals] = useState(20);
    const [isListSelection, setIsListSelection] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [refreshProgress, setRefreshProgress] = useState(0);
    const [watchedIps, setWatchedIps] = useState(new Set());
    const [watchlistRefreshTrigger, setWatchlistRefreshTrigger] = useState(0);

    useEffect(() => {
        loadData(true);
    }, [days]);

    // Auto-reload data every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            loadData();
        }, 10000); // 10 seconds

        return () => clearInterval(interval);
    }, [days]); // Depend on days so interval resets when days filter changes

    // Progress bar for auto-refresh countdown
    useEffect(() => {
        const progressInterval = setInterval(() => {
            setRefreshProgress(prev => {
                const newProgress = prev + 1; // Increment by 1% every 100ms (10 seconds total)
                if (newProgress >= 100) {
                    return 0; // Reset when reaching 100%
                }
                return newProgress;
            });
        }, 100); // Update every 100ms for smooth animation

        return () => clearInterval(progressInterval);
    }, []);

    const loadData = (firstLoad = false) => {
        if (firstLoad) setLoading(true);
        setRefreshProgress(0); // Reset progress when loading starts
        
        let logsLoaded = false;
        let statsLoaded = false;
        let logsData = [];
        let statsData = {};
        
        const checkCompletion = () => {
            if (logsLoaded && statsLoaded) {
                setLogs(logsData);
                setStats(statsData);
                setLastUpdated(new Date());
                setLoading(false);
                
                // Load watchlist to highlight watched IPs
                loadWatchedIps();
            }
        };
        
        // Load logs
        fetchAPI(`/ip-logs?days=${days}`, (response) => {
            logsData = response || [];
            logsLoaded = true;
            checkCompletion();
        });
        
        // Load stats
        fetchAPI(`/ip-stats?days=${days}`, (response) => {
            statsData = response || {};
            statsLoaded = true;
            checkCompletion();
        });
    };

    const loadWatchedIps = (forceRefresh = false) => {
        fetchAPI('/ip-watchlist', (response) => {
            if (response && response.ips) {
                const watchedIpSet = new Set(response.ips.map(item => item.ip));
                setWatchedIps(watchedIpSet);
            } else {
                setWatchedIps(new Set());
            }
        }, !forceRefresh); // Use cache only if not forcing refresh
    };

    // Filter out anonymous connections and group by 5-minute bins
    const processLogs = () => {
        // Only show authenticated users (exclude anonymous and invalid user IDs)
        const authenticatedLogs = logs.filter(log => 
            log.userId && 
            log.userId !== 'anonymous' && 
            log.userId !== 'null' && 
            log.userId !== -1 && 
            log.userId !== '-1'
        );

        // Group by 5-minute bins
        const groupedLogs = {};
        authenticatedLogs.forEach(log => {
            const date = new Date(log.timestamp);
            // Round down to 5-minute intervals
            const minutes = Math.floor(date.getMinutes() / 5) * 5;
            date.setMinutes(minutes, 0, 0); // Set to start of 5-minute bin
            const binKey = date.toISOString();
            
            if (!groupedLogs[binKey]) {
                groupedLogs[binKey] = {
                    timestamp: date,
                    userLocations: new Map(), // Store user-location pairs
                    entries: []
                };
            }
            
            // Create user-location pairs
            const userLocation = `${log.userId}|${log.city || log.country || 'Desconegut'}`;
            if (!groupedLogs[binKey].userLocations.has(userLocation)) {
                groupedLogs[binKey].userLocations.set(userLocation, {
                    userId: log.userId,
                    location: log.city || log.country || 'Desconegut',
                    count: 0
                });
            }
            groupedLogs[binKey].userLocations.get(userLocation).count += log.count || 1;
            groupedLogs[binKey].entries.push(log);
        });

        // Convert to array and sort by timestamp
        return Object.values(groupedLogs)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    };

    const processedLogs = processLogs();
    let filteredLogs = processedLogs;
    
    // Filter by country/city
    if (selectedCountry) {
        filteredLogs = filteredLogs.filter(logGroup => 
            logGroup.entries.some(log => (log.city || log.country) === selectedCountry)
        );
    }
    
    // Filter by user
    if (selectedUser) {
        filteredLogs = filteredLogs.filter(logGroup => 
            logGroup.entries.some(log => String(log.userId) === String(selectedUser))
        );
    }

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const endDate = new Date(date.getTime() + 5 * 60 * 1000); // Clone and add 5 minutes
        
        const day = date.toLocaleDateString('ca-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
        });
        
        const startTime = date.toLocaleTimeString('ca-ES', {
            hour: '2-digit',
            minute: '2-digit'
        });
        
        const endTime = endDate.toLocaleTimeString('ca-ES', {
            hour: '2-digit',
            minute: '2-digit'
        });
        
        return { day, time: `${startTime} - ${endTime}` };
    };

    const getUserDisplayName = (userId) => {
        // Don't display -1 or other invalid user IDs
        if (userId === -1 || userId === '-1' || !userId) {
            return null;
        }
        
        const user = castellersInfo?.[userId];
        if (user) {
            const parts = [];
            if (user.nom) parts.push(user.nom);
            if (user.cognom) parts.push(user.cognom);
            if (user.mote) parts.push(`(${user.mote})`);
            return parts.join(' ');
        }
        return userId;
    };

    // Calculate simplified stats for authenticated users only
    const getSimplifiedStats = () => {
        const authenticatedLogs = logs.filter(log => 
            log.userId && 
            log.userId !== 'anonymous' && 
            log.userId !== 'null' && 
            log.userId !== -1 && 
            log.userId !== '-1'
        );
        
        const uniqueUsers = new Set(authenticatedLogs.map(log => log.userId)).size;
        const uniqueCities = new Set(authenticatedLogs.map(log => log.city || log.country).filter(Boolean)).size;
        const uniqueIPs = new Set(authenticatedLogs.map(log => log.ip)).size;
        
        const cities = {};
        const users = {}; // Add user statistics
        authenticatedLogs.forEach(log => {
            const city = log.city || log.country;
            if (city) {
                cities[city] = (cities[city] || 0) + (log.count || 1);
            }
            
            // Count user activity
            if (log.userId) {
                users[log.userId] = (users[log.userId] || 0) + (log.count || 1);
            }
        });

        return {
            totalConnections: authenticatedLogs.reduce((total, log) => total + (log.count || 1), 0),
            uniqueUsers,
            uniqueCities,
            uniqueIPs,
            cities,
            users // Add users to the return object
        };
    };

    const simplifiedStats = getSimplifiedStats();

    const clearFilters = () => {
        setSelectedCountry('');
        setSelectedUser('');
        setVisibleIntervals(20);
        setIsListSelection(false);
    };

    // Handle location clicks from different sources
    const handleLocationClick = (location, source = 'list') => {
        if (source === 'map') {
            // Click from map: toggle filter (show/hide filter)
            setSelectedCountry(selectedCountry === location ? '' : location);
            setIsListSelection(false); // Map click is not a list selection
        } else {
            // Click from list: toggle filter (double click to clear)
            if (selectedCountry === location) {
                // If clicking the same location again, clear the filter
                setSelectedCountry('');
                setIsListSelection(false);
            } else {
                // If clicking a different location, select it
                setSelectedCountry(location);
                setIsListSelection(true); // This is a list selection
            }
        }
    };

    const handleIpClick = (ip) => {
        if (watchedIps.has(ip)) {
            if (window.confirm(`👁️ Aquesta IP ja està a la watchlist.\n\nVols eliminar-la de la watchlist?`)) {
                deleteAPI(`/ip-watchlist/${encodeURIComponent(ip)}`, (result) => {
                    // Update watched IPs directly from the response
                    if (result && result.watchlist) {
                        const watchedIpSet = new Set(result.watchlist.map(item => item.ip));
                        setWatchedIps(watchedIpSet);
                    }
                    // Trigger refresh of the IpWatchlist component
                    setWatchlistRefreshTrigger(prev => prev + 1);
                });
            }
            return;
        }
        
        if (!window.confirm(`Vols afegir l'IP ${ip} a la watchlist?`)) {
            return; // User cancelled
        }
        
        const label = prompt(`Escriu un label opcional per aquesta IP (ex: "Wifi de la colla rival"):`);
        if (label === null) return; // User cancelled
        
        postAPI('/ip-watchlist', { 
            ip: ip, 
            label: label || '' 
        }, (result) => {
            // Update watched IPs directly from the response
            if (result && result.watchlist) {
                const watchedIpSet = new Set(result.watchlist.map(item => item.ip));
                setWatchedIps(watchedIpSet);
            }
            setWatchlistRefreshTrigger(prev => prev + 1);
        });
    };

    return (
        <div className="mapa-accions-container">
            <UserInfo castellersInfo={castellersInfo} {...props} socket={socket} userInfo={userInfo} />
            <HeaderTabs castellersInfo={castellersInfo} userInfo={userInfo} {...props} />

            <Pressable style={{ backgroundColor: '#eee' }} className="boto-back" href='/'>
                ← Tornar a la pàgina principal
            </Pressable>

            <div className="mapa-accions-header">
                <h1>👥 Connexions d'Usuaris</h1>
                {lastUpdated && (
                    <div className="auto-reload-indicator">
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                <span>🔄</span>
                                <span>
                                    Última actualització: {lastUpdated.toLocaleTimeString('ca-ES')}
                                </span>
                            </div>
                            <div style={{ 
                                width: '175px', 
                                height: '2px', 
                                backgroundColor: '#e2e8f0', 
                                borderRadius: '1px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: `${refreshProgress}%`,
                                    height: '100%',
                                    backgroundColor: '#3b82f6',
                                    borderRadius: '1px',
                                    transition: 'width 0.1s ease-out'
                                }} />
                            </div>
                        </div>
                    </div>
                )}
                <div className="filters">
                    <select 
                        value={days} 
                        onChange={(e) => setDays(parseInt(e.target.value))}
                        className="days-filter"
                    >
                        <option value={1}>Últim dia</option>
                        <option value={7}>Última setmana</option>
                        <option value={30}>Últim mes</option>
                    </select>
                    
                    <select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        className="country-filter"
                    >
                        <option value="">Totes les ciutats</option>
                        {Object.keys(simplifiedStats.cities || {}).map(city => (
                            <option key={city} value={city}>
                                📍 {city} ({Math.ceil((simplifiedStats.cities[city] || 0) / 10)})
                            </option>
                        ))}
                    </select>
                    
                    {(selectedCountry || selectedUser) && (
                        <button onClick={clearFilters} className="clear-filters-button">
                            ✕ Netejar filtres
                        </button>
                    )}
                </div>
            </div>

            {(selectedCountry || selectedUser) && (
                <div className="active-filters">
                    <span>Filtres actius:</span>
                    {selectedCountry && (
                        <span className="filter-tag">
                            📍 {selectedCountry}
                            <button onClick={() => setSelectedCountry('')}>✕</button>
                        </span>
                    )}
                    {selectedUser && getUserDisplayName(selectedUser) && (
                        <span className="filter-tag">
                            👤 {getUserDisplayName(selectedUser)}
                            <button onClick={() => setSelectedUser('')}>✕</button>
                        </span>
                    )}
                </div>
            )}


            {loading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <div>Carregant...</div>
                </div>
            ) : (
                <>
                    <WorldMap 
                        logs={logs} 
                        selectedCountry={selectedCountry} 
                        onLocationClick={handleLocationClick}
                        isListSelection={isListSelection}
                    />
                    
                    <div className="content-grid">
                        <div className="logs-section">
                            <h2>📋 Qui ha entrat i des d'on?</h2>
                            <div className="logs-container">
                                {filteredLogs.length === 0 ? (
                                    <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                        No hi ha connexions d'usuaris registrats per mostrar
                                    </p>
                                ) : (
                                    filteredLogs.slice(0, visibleIntervals).map((logGroup, index) => {
                                        const { day, time } = formatDate(logGroup.timestamp);
                                        let userLocationPairs = Array.from(logGroup.userLocations.values());
                                        
                                        // Filter userLocationPairs by selected city if one is selected
                                        if (selectedCountry) {
                                            userLocationPairs = userLocationPairs.filter(pair => pair.location === selectedCountry);
                                        }
                                        
                                        // Filter userLocationPairs by selected user if one is selected
                                        if (selectedUser) {
                                            userLocationPairs = userLocationPairs.filter(pair => String(pair.userId) === String(selectedUser));
                                        }
                                        
                                        const showDateSeparator = index === 0 || new Date(filteredLogs[index - 1].timestamp).toDateString() !== new Date(logGroup.timestamp).toDateString();
                                        
                                        return (
                                            <div key={index}>
                                                {showDateSeparator && (
                                                    <div className="date-separator">
                                                        📅 {day}
                                                    </div>
                                                )}
                                                <div className="log-entry">
                                                    <div className="log-header">
                                                        <span className="log-time">
                                                            {time}
                                                        </span>
                                                    </div>
                                                    <div className="user-location-pairs">
                                                        {userLocationPairs.map((pair, idx) => {
                                                            // Get the first entry for this user-location pair to get IP
                                                            const firstEntry = logGroup.entries.find(entry => 
                                                                entry.userId === pair.userId && 
                                                                (entry.city || entry.country || 'Desconegut') === pair.location
                                                            );
                                                            
                                                            const userName = getUserDisplayName(pair.userId);
                                                            // Don't display entries for invalid users (like -1)
                                                            if (!userName) return null;
                                                            
                                                            return (
                                                                <div key={idx} className="user-location-pair">
                                                                    <span className="user-name">
                                                                        {userName}
                                                                    </span>
                                                                    <span className="location-badge">
                                                                        📍 {pair.location}
                                                                    </span>
                                                                    <span 
                                                                        className={`ip-badge ${watchedIps.has(firstEntry?.ip) ? 'watched' : ''}`}
                                                                        onClick={() => handleIpClick(firstEntry?.ip)}
                                                                        style={{ cursor: 'pointer' }}
                                                                        title={watchedIps.has(firstEntry?.ip) ? 'IP vigilada - Fes clic per gestionar' : 'Fes clic per afegir a la watchlist'}
                                                                    >
                                                                        🌐 {firstEntry?.ip || 'N/A'}
                                                                        {watchedIps.has(firstEntry?.ip) && ' 👁️'}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                
                                {filteredLogs.length > visibleIntervals && (
                                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                        <button 
                                            className="load-more-button"
                                            onClick={() => setVisibleIntervals(prev => prev + 20)}
                                        >
                                            Carrega més intervals
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="charts-section">
                            <div className="chart-card">
                                <h3>📍 Des de quines ciutats?</h3>
                                <div className="city-stats">
                                    {Object.entries(simplifiedStats.cities || {})
                                        .sort(([,a], [,b]) => b - a)
                                        .slice(0, 10)
                                        .map(([city, count]) => (
                                            <div 
                                                key={city} 
                                                className={`city-stat ${selectedCountry === city ? 'selected' : ''}`}
                                                onClick={() => handleLocationClick(city)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <span style={{ fontSize: '16px', fontWeight: '500' }}>
                                                    📍 {city}
                                                </span>
                                                <span className="count">{Math.ceil(count / 10)}</span>
                                            </div>
                                        ))}
                                </div>
                            </div>

                            <div className="chart-card">
                                <h3>👤 Qui és més actiu?</h3>
                                <div className="user-stats">
                                    {Object.entries(simplifiedStats.users || {})
                                        .sort(([,a], [,b]) => b - a)
                                        .slice(0, 10)
                                        .map(([userId, count]) => {
                                            const userName = getUserDisplayName(userId);
                                            // Don't display invalid users
                                            if (!userName) return null;
                                            
                                            return (
                                                <div 
                                                    key={userId} 
                                                    className={`user-stat ${String(selectedUser) === String(userId) ? 'selected' : ''}`}
                                                    onClick={() => {
                                                         setSelectedUser(String(selectedUser) === String(userId) ? '' : userId);
                                                     }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <span className="user-id">
                                                        {userName}
                                                    </span>
                                                    <span className="count">{Math.ceil(count / 10)}</span>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <IpWatchlist 
                        userInfo={userInfo} 
                        onWatchlistChange={(watchlistData) => {
                            if (watchlistData && watchlistData.watchlist) {
                                const watchedIpSet = new Set(watchlistData.watchlist.map(item => item.ip));
                                setWatchedIps(watchedIpSet);
                            }
                        }} 
                        refreshTrigger={watchlistRefreshTrigger}
                    />
                </>
            )}
        </div>
    );
}

export default MapaAccionsApp;